import { compact as _compact, startCase as _startCase } from 'lodash-es'
import type { ProvetPayPaymentListItemAttributes } from '@/models/reports/provetPayPaymentReport.js'
import { toNumber } from '@/utils/number.js'
import { paymentImageAssets } from '@/utils/paymentImages.js'

const canonicalize = (s: string) => s.toLowerCase().replace(/[\W_]/g, '')

const LABELS: Record<string, string> = {
  amex: 'American Express',
  applepay: 'Apple Pay',
  googlepay: 'Google Pay',
  mastercard: 'Mastercard',
  mc: 'Mastercard',
}

const IMAGE_KEY_ALIASES: Record<string, string> = {
  mc: 'mastercard',
}

const WALLET_KEYS = new Set(['applepay', 'googlepay'])

const imagesByKey: Record<string, string> = Object.fromEntries(
  Object.entries(paymentImageAssets).map(([path, url]) => [
    canonicalize(
      path
        .split('/')
        .at(-1)!
        .replace(/\.png$/, ''),
    ),
    url,
  ]),
)

const splitTokens = (raw: string | null | undefined): string[] =>
  _compact((raw ?? '').split(/[\s_]+/))

const tokenLabel = (token: string): string =>
  LABELS[canonicalize(token)] ?? _startCase(token)

const lookupImage = (key: string): string | undefined =>
  imagesByKey[IMAGE_KEY_ALIASES[key] ?? key]

const detectWalletKey = (raw: string): string | undefined =>
  [...WALLET_KEYS].find((k) => canonicalize(raw).includes(k))

export const normalizePaymentMethod = (
  raw: string | null | undefined,
): string => splitTokens(raw).map(tokenLabel).join(' ')

export const getPaymentMethodImage = (
  raw: string | null | undefined,
): string | undefined => {
  if (!raw) {
    return undefined
  }
  const directImage = lookupImage(canonicalize(raw))
  if (directImage) {
    return directImage
  }
  const walletKey = detectWalletKey(raw)
  if (walletKey) {
    return lookupImage(walletKey)
  }
  const tokens = splitTokens(raw)
  return lookupImage(canonicalize(tokens[0] ?? ''))
}

export const getPaymentMethodData = (raw: string | null | undefined) => {
  const canonical = canonicalize(raw ?? '')
  const walletKey = detectWalletKey(raw ?? '')
  const brandCanonical = walletKey
    ? canonical.replace(walletKey, '')
    : canonical

  return {
    brandLabel: brandCanonical
      ? (LABELS[IMAGE_KEY_ALIASES[brandCanonical] ?? brandCanonical] ??
        _startCase(brandCanonical))
      : '',
    fullLabel: normalizePaymentMethod(raw),
    image: getPaymentMethodImage(raw),
    isCompound: Boolean(walletKey && brandCanonical),
  }
}

export const getMoneyAbsoluteValue = (value: unknown) =>
  Math.abs(toNumber(value))

// Nh_markup and processing fee are parts of nordhealth_fee, so we don't include them to calculations
export const calculateProvetPayFees = (
  attributes: ProvetPayPaymentListItemAttributes,
) =>
  getMoneyAbsoluteValue(attributes.nordhealth_fee) +
  getMoneyAbsoluteValue(attributes.other_fees) +
  getMoneyAbsoluteValue(attributes.reversal_fee)
